<script>
  import SubMenu from "./components/subMenu.svelte"
  import ApplicationState from "../../Stores/applicationStates"
  import sitemap from "../../Routes/sitemap"
  import { push } from "svelte-spa-router"
  import notify from "../../Script/notify"
  import UserInfo from "./modules/userInfo.svelte"
  import FeatureState from "../../Stores/featureState"
  import { getRequest, postRequest } from "../../Script/apiMethods"
  import LoadingScreen from "../../Pages/Publish/SinglePublish/Components/loadingScreen.svelte"

  /**
   * Indicates whether user information should be displayed.
   * Defaults to false.
   * @type {boolean}
   */
  let showUserInfo = false
  /**
   * Indicates whether showing the new/ updated iunformation.
   * Defaults to false.
   * @type {boolean}
   */

  /**
   * Represents submenu items based on the current hash location.
   * Defaults to an empty array if no submenu items are found.
   * @type {Array<Object>}
   */
  let subMenuItems = sitemap[location.hash.split("/")[1]] || []

  /**
   * Indicates whether the menu should be shown in mobile view.
   * Defaults to false.
   * @type {boolean}
   */
  let showMenuInMobile = false

  /**
   * Holds the currently selected mobile item.
   * @type {string}
   */
  let mobileItem = ""

  let showLoading = false

  /**
   * @typedef {Object} closeNotification
   * @property {Function} close - Closes the notification.
   */
  let closeNotification = {
    close() {},
  }

  /**
   * @function - Handles the logout process.
   * Sets showMenuInMobile to false, updates application state to clear the token,
   * navigates to the home page, displays a success notification, and clears local storage.
   * @returns {Promise<void>}
   */
  const onLogout = async () => {
    // Set showMenuInMobile to false to hide mobile menu if open
    showMenuInMobile = false

    // Adjust body scroll behavior based on showMenuInMobile state
    mobileNavScrollControl()

    // Update application state to clear the token
    ApplicationState.update((state) => {
      state.token = null
      state.msignToken = null
      return state
    })

    // Navigate to the home page
    push("/")

    // Show success notification
    notify.success("Successfully Logged Out")

    // Backup the required data
    const preservedData = localStorage.getItem("P2BUpdate")

    // Clear all localStorage
    localStorage.clear()

    // Restore the preserved data
    if (preservedData !== null) {
      localStorage.setItem("P2BUpdate", preservedData)
    }
  }

  /**
   * @function - Controls the body scroll behavior on mobile based on showMenuInMobile state.
   */
  const mobileNavScrollControl = () => {
    if (showMenuInMobile) document.body.style.overflow = "hidden"
    else document.body.style.overflow = "auto"
  }

  const checkSigningStatus = async () => {
    try {
      showLoading = true

      const CONFIG = {
        headers: {
          "x-access-token": $ApplicationState.token,
        },
      }

      // Get Claim and signature
      const { claim, signature } = await getRequest("/api/swagger/v2/misc/claim?system=MULTI-SIGN", CONFIG)
      if (!claim || !signature) throw "P2BCODE::ERROR_P2B_NOTIFIED"

      // Get Msign Token for multisigner
      const { token } = await postRequest("/api/pdf-signing/v2/auth/by-common", { claim, signature })
      if (!token) throw "P2BCODE::ERROR_P2B_NOTIFIED"
      $ApplicationState.msignToken = token

      return true
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "PAGE: Navbar\nEVENT: While checking signing availability."
      closeNotification = notify.danger("Signing is currently unavailable. Please try again later.", info)
      return false
    } finally {
      showLoading = false
    }
  }
</script>

{#if showLoading}
  <LoadingScreen msg="Checking Signing Availability" />
{/if}

<!--navbar-->
<div
  class="{showMenuInMobile ? 'absolute inset-0 z-50 h-screen' : ''} select-none"
  on:mouseleave={() => {
    subMenuItems = sitemap[location.hash.split("/")[1]]
  }}
>
  <div class="{showMenuInMobile ? 'h-screen sm:h-auto' : ''} via-75% flex w-full flex-col items-center justify-between bg-gradient-to-r from-[#FF6A00] via-[#F8A500] to-[#FF8000] px-4 py-4 text-white dark:bg-gradient-to-r dark:from-black dark:via-black dark:to-black sm:flex sm:flex-row md:px-5">
    <div class="flex w-full flex-col sm:w-auto sm:flex-row sm:items-center">
      <div class="flex w-full cursor-pointer items-center justify-between gap-2 sm:w-auto">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <a href="#/analytics/documents" target="_blank" class="flex w-full items-center gap-3">
          <button class="flex h-10 w-10 items-center justify-center rounded-md hover:animate-rotate">
            <svg class=" {$ApplicationState.darkMode ? 'dark' : ''}" id="Layer_3" data-name="Layer 4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 727.6 594.77">
              <defs />
              <g id="Layer_3" data-name="Layer 4">
                <g>
                  <path class="cls-3" d="m363.8,250.05l-229.9,109.87,229.9,109.88v124.97L55.03,447.21c-73.38-35.07-73.38-139.52,0-174.59L363.8,125.03v125.02Z" />
                  <path class="cls-3" d="m363.8,125.02l229.9,109.87-229.9,109.88v124.97l308.76-147.56c73.38-35.07,73.38-139.52,0-174.59L363.8,0v125.02Z" />
                </g>
              </g>
            </svg>
          </button>
          <span class="block text-lg font-bold sm:hidden">DocChain.io</span>
        </a>
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
          on:click={() => {
            // Toggle mobile menu visibility
            showMenuInMobile = !showMenuInMobile
            // Update submenu items based on hash location
            subMenuItems = sitemap[location.hash.split("/")[1]]
            // Set mobileItem to the current submenu items
            mobileItem = subMenuItems
            // Adjust body scroll behavior on mobile
            mobileNavScrollControl()
          }}
          class="ml-auto flex aspect-square h-5 cursor-pointer items-center justify-center sm:hidden"
        >
          <i class="fa-solid fa-ellipsis-vertical" />
        </div>
      </div>
      <div class="{showMenuInMobile ? 'block' : 'hidden'} mt-4 space-y-2 font-semibold mobile:pl-4 sm:mt-0 sm:flex sm:space-x-2 sm:space-y-0 xl:space-x-6 xl:pl-6 2xl:space-x-10 2xl:pl-10">
        {#each Object.keys(sitemap) as item}
          <!-- {#if item !== "signing" || ($ApplicationState.pdfEnabled && $ApplicationState.msignToken)} -->
          {#if item !== "signing" || $ApplicationState.pdfEnabled}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div>
              <!-- svelte-ignore a11y-mouse-events-have-key-events -->
              <a
                href={window.innerWidth > 640 && subMenuItems ? subMenuItems[0].route : "#"}
                on:click={async () => {
                  if (item == "signing" && $ApplicationState.pdfEnabled && !$ApplicationState.msignToken) {
                    const isAvailable = await checkSigningStatus()
                    if (isAvailable) {
                      subMenuItems = sitemap[item]
                      // if (window.innerWidth > 640) push(subMenuItems[0].route)
                      // Update mobileItem to the clicked item
                      mobileItem = item
                    }
                  } else {
                    // Update subMenuItems to display the submenu items corresponding to the clicked item
                    subMenuItems = sitemap[item]
                    // if (window.innerWidth > 640) push(subMenuItems[0].route)
                    // Update mobileItem to the clicked item
                    mobileItem = item
                  }
                }}
                class="hidden cursor-pointer rounded-md mobile:block {subMenuItems == sitemap[item] ? 'border-white dark:border-[#FF6A00]' : ''} border-2 sm:rounded-full {location.hash.indexOf(item) == 2 ? 'bg-white text-[#FF6A00] dark:bg-[#FF6A00] dark:text-white ' : ''} w-full border-transparent px-2 py-1 capitalize hover:border-white dark:hover:border-[#FF6A00] sm:text-base md:px-3 md:text-lg"
              >
                {item}
                <!-- on:mouseover={() => {
              if (window.innerWidth > 640) {
                // Update subMenuItems on mouseover if window width is greater than 640 pixels
                subMenuItems = sitemap[item]
                // Update mobileItem to the hovered item
                mobileItem = item
              }
            }} -->
              </a>
              <!-- svelte-ignore a11y-mouse-events-have-key-events -->
              <div
                on:click={() => {
                  // Update subMenuItems to display the submenu items corresponding to the clicked item
                  subMenuItems = sitemap[item]
                  // Update mobileItem to the hovered item
                  mobileItem = item
                }}
                class="block cursor-pointer rounded-md mobile:hidden {subMenuItems == sitemap[item] ? 'border-white dark:border-[#FF6A00]' : ''} border-2 sm:rounded-full {location.hash.indexOf(item) == 2 ? 'bg-white text-[#FF6A00] dark:bg-[#FF6A00] dark:text-white ' : ''} w-full border-transparent px-3 py-1 capitalize hover:border-white dark:hover:border-[#FF6A00] sm:text-base md:text-lg"
              >
                {item}
                <!-- on:mouseover={() => {
              if (window.innerWidth > 640) {
                // Update subMenuItems to display the submenu items corresponding to the clicked item
                subMenuItems = sitemap[item]
                // Update mobileItem to the hovered item
                mobileItem = item
              }
            }} -->
              </div>
              <div
                on:click={() => {
                  showMenuInMobile = false
                  mobileNavScrollControl()
                }}
                class="sm:hidden {mobileItem == item ? 'block' : 'hidden'}"
              >
                <SubMenu items={subMenuItems} />
              </div>
            </div>
          {/if}
        {/each}
      </div>
    </div>
    <div class="flex items-center gap-3">
      <button on:click={() => (showUserInfo = true)} class="{showMenuInMobile ? 'flex' : 'hidden'} mb-12 mt-1 h-8 w-8 cursor-pointer items-center justify-center font-semibold mobile:mb-0 mobile:pb-1 sm:ml-auto sm:mt-1 sm:flex">
        <i class="fa-solid fa-circle-user fa-xl text-white hover:text-blue-500" />
      </button>

      <!-- <button
        on:click={() => {
          $FeatureState.showUpdates = true
        }}
        class="{showMenuInMobile ? 'flex' : 'hidden'} relative mt-1 mb-12 h-8 w-8 cursor-pointer items-center justify-center rounded-full font-semibold text-white dark:text-gray-300 mobile:mb-0 mobile:pb-1 mobile:pt-1 sm:ml-auto sm:mt-0 sm:flex"
      >
        {#if $FeatureState.notifyUser}
          <i class="fa-solid fa-bell fa-xl fa-shake text-white hover:text-blue-500 dark:text-orange-500 dark:hover:text-blue-500" />
          <div class="absolute -top-1 -right-1 h-3 w-3 animate-ping rounded-full bg-red-500" />
          <div class="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500" />
        {:else}
          <i class="fa-solid fa-bell fa-xl text-white hover:text-blue-500" />
        {/if}
      </button> -->

      <!-- <NewUpdates {showMenuInMobile} /> -->

      <button on:click={onLogout} class="{showMenuInMobile ? 'flex' : 'hidden'} mb-12 mt-2 flex aspect-square cursor-pointer items-center justify-center rounded-full border-transparent font-semibold hover:border-white dark:hover:border-[#FF6A00] mobile:mb-0 mobile:py-1 sm:ml-auto sm:mt-0 sm:flex sm:w-[35px] sm:border-2 lg:aspect-auto lg:w-auto lg:px-3">
        <i class="fa-solid fa-right-from-bracket" />
        <p class="sm:hidden sm:text-base md:text-lg lg:block">&nbsp Logout</p>
      </button>
    </div>
  </div>
  <div class="hidden sm:block">
    <SubMenu items={subMenuItems} />
  </div>
</div>

{#if showUserInfo}
  <UserInfo state={$ApplicationState} on:click={() => (showUserInfo = !showUserInfo)} bind:showUserInfo />
{/if}

<style lang="postcss">
  .cls-3 {
    fill: #fff;
  }
  .dark .cls-3 {
    fill: #ff6a00;
  }
</style>
